import React from "react";
import {
  Mail,
  Phone,
  Printer,
  MapPin,
  FileText,
  Award,
  ShieldCheck,
} from "lucide-react";
import { Practitioner } from "@/lib/types";

interface PractitionerInfoCardProps {
  practitioner: Practitioner;
}

const PractitionerInfoCard: React.FC<PractitionerInfoCardProps> = ({
  practitioner,
}) => {
  const fullAddress = [
    practitioner.office_address_line_1,
    practitioner.office_address_line_2,
    practitioner.office_city,
    practitioner.stateInfo?.name,
    practitioner.office_postal_code,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Professional Credentials */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "#4A8A9A" }}
            >
              <ShieldCheck className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Professional Credentials
              </h3>
              <p className="text-xs text-slate-500">
                Licensing & certification
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* NPI - Featured */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: "rgb(245, 247, 247)" }}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                NPI Number
              </span>
            </div>
            <p className="text-lg font-semibold text-slate-900 font-mono">
              {practitioner.npi || "—"}
            </p>
          </div>

          {/* DEA & License */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Award className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  DEA Number
                </span>
              </div>
              <p className="text-sm font-medium text-slate-900 font-mono">
                {practitioner.dea || "—"}
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  License #
                </span>
              </div>
              <p className="text-sm font-medium text-slate-900 font-mono">
                {practitioner.license_number || "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "#BD6F51" }}
            >
              <Mail className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">
                Contact Information
              </h3>
              <p className="text-xs text-slate-500">
                Communication details
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Email */}
          <div className="flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgb(245, 247, 247)" }}
            >
              <Mail className="h-4 w-4 text-slate-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                Email
              </p>
              <p className="text-sm text-slate-900 truncate">
                {practitioner.email || "—"}
              </p>
            </div>
          </div>

          {/* Phone & Fax */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgb(245, 247, 247)" }}
              >
                <Phone className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                  Phone
                </p>
                <p className="text-sm text-slate-900 truncate">
                  {practitioner.phone || "—"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgb(245, 247, 247)" }}
              >
                <Printer className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                  Fax
                </p>
                <p className="text-sm text-slate-900 truncate">
                  {practitioner.fax || "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{ backgroundColor: "rgb(245, 247, 247)" }}
          >
            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-white">
              <MapPin className="h-4 w-4 text-slate-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">
                Office Address
              </p>
              <p className="text-sm text-slate-900 leading-relaxed">
                {fullAddress || "No address on file"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PractitionerInfoCard;
